/**
 * Take-Home Pay tile (BUILD-SPEC.md §3.1) — the first tile, built to exercise
 * the whole shell: it reads its state from the URL fragment, computes through
 * the deterministic tax engine, shows every line with its citation, and offers
 * a worked example (§2 principle 6). The other Pillar 1 tiles in Phase 5 follow
 * this exact pattern.
 */
import { Money, allocateRounded } from "../engine/money";
import { evaluateTaxes, type TaxInput, type TaxResult } from "../engine/tax";
import type { DeductionMode } from "../engine/tax/types";
import type { CitationData, FilingStatus } from "../data/schemas";
import { el, option } from "../ui/dom";
import { NO_STATE_OPTION_LABEL, field, parseNonNegative, pct, tryExampleButton } from "../ui/form";
import { citationLink, resultCard, type BreakdownLine } from "../ui/resultCard";
import { rememberableCounty, residenceLocalField, seedResidenceLocal } from "../ui/residenceLocal";
import { statutoryStepNote } from "../ui/statuteStep";
import { rememberShared } from "./profileSync";
import type { SituationStore } from "../profile/situation";
import type { TileContext, TileDefinition } from "./types";
import { OBBBA_DEDUCTIONS_HOW_NO_GIVING } from "./deductionCopy";

const FILING_STATUSES: { value: FilingStatus; label: string }[] = [
  { value: "single", label: "Single" },
  { value: "married_jointly", label: "Married filing jointly" },
  { value: "married_separately", label: "Married filing separately" },
  { value: "head_of_household", label: "Head of household" },
  { value: "qualifying_surviving_spouse", label: "Qualifying surviving spouse" },
];

/** IRC §151(d)(5)(C) reaches the taxpayer and, on a joint return, the spouse. */
const SENIOR_COUNTS = [
  { value: "0", label: "Neither of us" },
  { value: "1", label: "One of us" },
  { value: "2", label: "Both of us (joint return)" },
];

/**
 * This tile computes on the STANDARD deduction, and no longer offers a choice.
 *
 * It used to offer three: "Larger of standard / itemized", "Standard
 * deduction", and "Itemized (big four)" — while asking for none of the big
 * four. So two of the options did the same thing and the third computed an
 * itemized total of zero. A single filer earning $85,000 in California who
 * picked "Itemized", which is exactly what an itemizer would pick, was told
 * their take-home was $61,272.52 when it is $64,814.52: $3,542 of tax they do
 * not owe, from one visible dropdown.
 *
 * The rule this tile already followed for deductions — never claim one you have
 * no input for — applies to a CHOICE the same way. The Federal Income Tax tile
 * asks for the big four and makes the comparison honestly; this one says so.
 */
const DEDUCTION_MODE: DeductionMode = "standard";

interface Fields {
  fs: FilingStatus;
  st: string; // state code, or "" for federal-only
  wages: number;
  other: number;
  adjustments: number;
  /** How many on the return are 65 or over (IRC §151(d)(5)(C)). */
  seniors: number;
  /** Qualified tips inside `wages` (IRC §224). */
  tips: number;
  /** Qualified overtime premium inside `wages` (IRC §225). */
  overtime: number;
  local: string[]; // selected local add-on ids
  /** A second state to set the same return against, or "" for none (SPEC-5 §2.1). */
  vs: string;
}

const EXAMPLE: Fields = {
  fs: "single",
  st: "ca",
  wages: 85000,
  other: 0,
  adjustments: 0,
  seniors: 0,
  tips: 0,
  overtime: 0,
  local: [],
  vs: "",
};

function isFilingStatus(v: string): v is FilingStatus {
  return FILING_STATUSES.some((f) => f.value === v);
}
function readFields(
  params: URLSearchParams,
  defaultState: string,
  profile: SituationStore,
): Fields {
  const fsRaw = params.get("fs");
  const stRaw = params.get("st");
  return {
    // Precedence: URL fragment > session profile > built-in default.
    fs: fsRaw && isFilingStatus(fsRaw) ? fsRaw : (profile.get("filingStatus") ?? "single"),
    st: stRaw !== null ? stRaw : (profile.get("stateCode") ?? defaultState),
    wages: params.has("w")
      ? parseNonNegative(params.get("w"), 0)
      : (profile.get("annualIncome") ?? 0),
    other: parseNonNegative(params.get("oi"), 0),
    // "Pre-tax adjustments" on screen, `preTaxContributions` in My Situation —
    // the same number under two names, and until 2026-09-02 the profile field
    // was declared, documented, and read by nothing. Same precedence as every
    // other shared field: the link wins, then the profile, then zero.
    adjustments: params.has("adj")
      ? parseNonNegative(params.get("adj"), 0)
      : (profile.get("preTaxContributions") ?? 0),
    // Clamped where it is read: a hostile deep link can say anything, and the
    // select has no option for "7".
    seniors: Math.min(2, Math.max(0, Math.round(parseNonNegative(params.get("age65"), 0)))),
    // The Readout writes these from W-2 box 12 codes TP and TT, so a reader who
    // dropped their W-2 in finds the two fields already filled. Same precedence
    // as every other shared field: the link wins, then the profile, then zero.
    tips: params.has("tips")
      ? parseNonNegative(params.get("tips"), 0)
      : (profile.get("qualifiedTipsAnnual") ?? 0),
    overtime: params.has("ot")
      ? parseNonNegative(params.get("ot"), 0)
      : (profile.get("qualifiedOvertimeAnnual") ?? 0),
    local: (params.get("loc") ?? "").split(",").filter((s) => s.length > 0),
    vs: params.get("vs") ?? "",
  };
}

function writeFields(f: Fields): URLSearchParams {
  const p = new URLSearchParams();
  p.set("fs", f.fs);
  p.set("st", f.st);
  p.set("w", String(f.wages));
  if (f.other > 0) p.set("oi", String(f.other));
  if (f.seniors > 0) p.set("age65", String(f.seniors));
  // Written whether or not they are zero, unlike the fields above them. Each of
  // these three falls back to My Situation when the link is silent, so dropping
  // a zero does not restore a zero -- it hands the question to the READER's
  // profile, and a link that says "no tips, no overtime, no pre-tax
  // adjustments" reopens with the reader's own three figures and a smaller
  // take-home than the sender computed. A zero is an answer here.
  p.set("adj", String(f.adjustments));
  p.set("tips", String(f.tips));
  p.set("ot", String(f.overtime));
  if (f.local.length > 0) p.set("loc", f.local.join(","));
  if (f.vs) p.set("vs", f.vs);
  return p;
}

/**
 * The column a reader can add up.
 *
 * Every part here is correct to the cent on its own, and rounding each on its
 * own is still wrong as a column: `sum(round(xᵢ))` and `round(sum(xᵢ))` differ
 * by a cent in roughly one Take-Home case in fourteen, so somebody adding
 * federal, FICA, state and local got a number the "Total tax" line beside it
 * disagreed with — on the site whose whole claim is that its arithmetic can be
 * checked.
 *
 * The total stays engine-exact and the parts are allocated to it by largest
 * remainder ({@link allocateRounded}). Quantizing the total in the engine
 * instead was tried and is worse: the combined marginal rate is measured over a
 * $100 wage probe, so cents of noise in the total become hundredths of a point
 * in a printed rate, and a hand-verified §68 identity stopped holding.
 */
function buildBreakdown(result: TaxResult, locale: string): BreakdownLine[] {
  const fmt = (m: Money): string => m.format(locale);

  /** Every line that is a share of Total tax, in the order they are shown. */
  const parts: { label: string; amount: Money; citation?: CitationData }[] = [
    {
      label: `Federal income tax (${result.federal.deduction.kind} deduction)`,
      amount: result.federal.incomeTax,
      citation: result.federal.citation,
    },
    {
      label: "Social Security (FICA)",
      amount: result.fica.socialSecurity,
      citation: result.fica.citation,
    },
    { label: "Medicare (FICA)", amount: result.fica.medicare, citation: result.fica.citation },
  ];
  if (result.fica.additionalMedicare.greaterThan(0)) {
    parts.push({
      label: "Additional Medicare",
      amount: result.fica.additionalMedicare,
      citation: result.fica.citation,
    });
  }
  if (result.state && result.state.incomeTax.greaterThan(0)) {
    parts.push({
      label: `${result.state.jurisdictionName} income tax`,
      amount: result.state.incomeTax,
      citation: result.state.citation,
    });
  }
  for (const line of result.local.lines) {
    parts.push({
      label: `${line.name} local tax`,
      amount: line.tax,
      citation: result.local.citation ?? undefined,
    });
  }

  const shown = allocateRounded(
    parts.map((p) => p.amount),
    result.totals.totalTax,
  );

  return [
    { label: "Gross income", value: fmt(result.grossIncome) },
    ...parts.map((p, i) => ({
      label: p.label,
      value: fmt(shown[i]!),
      ...(p.citation ? { citation: p.citation } : {}),
    })),
    { label: "Total tax", value: fmt(result.totals.totalTax), emphasis: true },
    { label: "Effective rate", value: pct(result.totals.effectiveRate) },
    { label: "Marginal rate (next dollar)", value: pct(result.totals.marginalRate) },
  ];
}

export function mountTakeHome(ctx: TileContext): void {
  const { root, data } = ctx;
  root.replaceChildren();

  if (!data) {
    root.append(el("p", { class: "tile-error", text: "Tax data could not be loaded." }));
    return;
  }
  const federal = data.federal();
  const fica = data.fica();
  if (!federal || !fica) {
    root.append(
      el("div", {
        class: "verify-banner",
        attrs: { role: "alert" },
        text: "Federal tax data is unavailable, verify before relying on any figure.",
      }),
    );
    return;
  }

  // `data`, `federal`, and `fica` are non-null past the guards above; capture
  // them as locals so the nested closures keep the narrowed (non-null) types.
  const bundled = data;
  const fed = federal;
  const ficaData = fica;
  const codes = bundled.stateCodes();
  const defaultState = codes.includes("ca") ? "ca" : (codes[0] ?? "");
  let fields = readFields(ctx.params, defaultState, ctx.profile);

  // --- Controls ---
  const fsSelect = el(
    "select",
    { name: "fs", attrs: { "aria-label": "Filing status" } },
    ...FILING_STATUSES.map((s) => option(s.value, s.label, s.value === fields.fs)),
  );

  const stSelect = el(
    "select",
    { name: "st", attrs: { "aria-label": "State" } },
    option("", NO_STATE_OPTION_LABEL, fields.st === ""),
    ...codes.map((code) => {
      const j = bundled.state(code);
      return option(code, j ? j.name : code.toUpperCase(), code === fields.st);
    }),
  );
  // Make the element's value authoritative for the intended state, so a later
  // collect() reads the deep-linked state (not a default) regardless of
  // option-attribute timing.
  stSelect.value = fields.st;
  // The moving-states question (SPEC-5 §2.1): the same return in a second state,
  // so a reader weighing a move does not need two tabs.
  const vsSelect = el(
    "select",
    { name: "vs", attrs: { "aria-label": "Compare with another state" } },
    option("", "No comparison", fields.vs === ""),
    ...codes.map((code) => {
      const j = bundled.state(code);
      return option(code, j ? j.name : code.toUpperCase(), code === fields.vs);
    }),
  );
  vsSelect.value = codes.includes(fields.vs) ? fields.vs : "";

  const wagesInput = el("input", {
    type: "number",
    name: "w",
    min: 0,
    step: 1000,
    value: fields.wages,
    attrs: { "aria-label": "Annual wages", inputmode: "decimal" },
  });
  const otherInput = el("input", {
    type: "number",
    name: "oi",
    min: 0,
    step: 500,
    value: fields.other,
    attrs: { "aria-label": "Other income", inputmode: "decimal" },
  });
  const adjInput = el("input", {
    type: "number",
    name: "adj",
    min: 0,
    step: 500,
    value: fields.adjustments,
    attrs: { "aria-label": "Pre-tax adjustments", inputmode: "decimal" },
  });
  const seniorSelect = el(
    "select",
    { name: "age65", attrs: { "aria-label": "How many of you are 65 or older" } },
    ...SENIOR_COUNTS.map((c) => option(c.value, c.label, Number(c.value) === fields.seniors)),
  );

  const mkWagePart = (name: string, value: number, label: string): HTMLInputElement =>
    el("input", {
      type: "number",
      name,
      min: 0,
      step: 500,
      value,
      attrs: { "aria-label": label, inputmode: "decimal" },
    });
  const tipsInput = mkWagePart("tips", fields.tips, "Qualified tips included in your wages");
  const otInput = mkWagePart(
    "ot",
    fields.overtime,
    "Qualified overtime premium included in your wages",
  );

  const localContainer = el("div", { class: "local-addons" });
  const resultContainer = el("div", { class: "tile-result", attrs: { "aria-live": "polite" } });

  // A residence-based local tax (Maryland's county tax) is mandatory and set by
  // where you live, so it is a REQUIRED single-select — exactly one applies. Make
  // sure a valid county is selected, defaulting to the shard's default, so the
  // displayed Maryland tax always includes the county portion (never a stale or
  // missing one). Opt-in locals (NYC, Columbus) keep their multi-checkbox set.
  function resolveLocal(): void {
    fields.local = seedResidenceLocal(
      fields.st ? bundled.state(fields.st) : null,
      fields.local,
      ctx.profile,
    );
  }

  function renderLocalAddOns(): void {
    localContainer.replaceChildren();
    const state = fields.st ? bundled.state(fields.st) : null;
    const addOns = state?.localAddOns ?? [];
    if (addOns.length === 0) return;
    const county = residenceLocalField(state, fields.local[0], recompute);
    if (county) {
      localContainer.append(county);
      return;
    }
    localContainer.append(el("p", { class: "field-group-label", text: "Local taxes" }));
    for (const addOn of addOns) {
      const cb = el("input", {
        type: "checkbox",
        name: `loc-${addOn.id}`,
        checked: fields.local.includes(addOn.id),
        attrs: { "aria-label": addOn.name },
        on: { change: () => recompute() },
      });
      cb.id = `loc-${addOn.id}`;
      localContainer.append(
        el("label", { class: "checkbox" }, cb, el("span", { text: addOn.name })),
      );
    }
  }

  function collect(): void {
    const countySelect = localContainer.querySelector<HTMLSelectElement>(
      "select[name='loc-select']",
    );
    const local = countySelect
      ? countySelect.value
        ? [countySelect.value]
        : []
      : Array.from(localContainer.querySelectorAll<HTMLInputElement>("input:checked")).map((cb) =>
          cb.name.replace(/^loc-/, ""),
        );
    fields = {
      fs: isFilingStatus(fsSelect.value) ? fsSelect.value : "single",
      st: stSelect.value,
      wages: parseNonNegative(wagesInput.value, 0),
      other: parseNonNegative(otherInput.value, 0),
      adjustments: parseNonNegative(adjInput.value, 0),
      seniors: Math.min(2, Math.max(0, Math.round(parseNonNegative(seniorSelect.value, 0)))),
      tips: parseNonNegative(tipsInput.value, 0),
      overtime: parseNonNegative(otInput.value, 0),
      local,
      vs: vsSelect.value,
    };
    resolveLocal();
  }

  function compute(): void {
    const input: TaxInput = {
      filingStatus: fields.fs,
      wages: fields.wages,
      otherIncome: fields.other,
      adjustments: fields.adjustments,
      deductionMode: DEDUCTION_MODE,
      seniorsAge65Plus: fields.seniors,
      qualifiedTips: fields.tips,
      qualifiedOvertime: fields.overtime,
      localJurisdictionIds: fields.local,
    };
    const state = fields.st ? (bundled.state(fields.st) ?? undefined) : undefined;
    const result = evaluateTaxes(input, { federal: fed, fica: ficaData, state });

    const rateNote = statutoryStepNote(result, state, ctx.locale);
    resultContainer.replaceChildren(
      resultCard({
        label: "Annual take-home pay",
        value: result.totals.takeHome,
        locale: ctx.locale,
        breakdown: buildBreakdown(result, ctx.locale),
        permalink: () => ctx.permalink(writeFields(fields)),
      }),
      ...(rateNote ? [rateNote] : []),
      ...compareLine(input, result),
    );
  }

  /** The same return in the comparison state: no local taxes, which are the first state's. */
  function compareLine(input: TaxInput, result: TaxResult): HTMLElement[] {
    const other = fields.vs && fields.vs !== fields.st ? bundled.state(fields.vs) : null;
    if (!other) return [];
    const there = evaluateTaxes(
      { ...input, localJurisdictionIds: [] },
      { federal: fed, fica: ficaData, state: other },
    );
    const diff = there.totals.takeHome.subtract(result.totals.takeHome);
    const amount = (diff.isNegative() ? diff.multiply(-1) : diff).format(ctx.locale);
    return [
      el(
        "p",
        { class: "th-compare" },
        el("span", {
          text: `In ${other.name}, the same pay takes home ${there.totals.takeHome.format(ctx.locale)} a year: ${diff.isZero() ? "the same" : `${amount} ${diff.isNegative() ? "less" : "more"}`}, before any local tax there. `,
        }),
        citationLink(other.citation),
      ),
    ];
  }

  function recompute(): void {
    collect();
    renderLocalAddOns();
    ctx.setParams(writeFields(fields));
    rememberShared(ctx.profile, {
      filingStatus: fields.fs,
      stateCode: fields.st,
      county: rememberableCounty(fields.st ? bundled.state(fields.st) : null, fields.local),
      annualIncome: fields.wages,
      preTaxContributions: fields.adjustments,
      qualifiedTipsAnnual: fields.tips,
      qualifiedOvertimeAnnual: fields.overtime,
    });
    compute();
  }

  // A state the reader is only weighing is not where they live, so changing it
  // updates the link and the comparison and never writes My Situation.
  vsSelect.addEventListener("change", () => {
    collect();
    ctx.setParams(writeFields(fields));
    compute();
  });
  for (const control of [fsSelect, stSelect, seniorSelect]) {
    control.addEventListener("change", recompute);
  }
  for (const input of [wagesInput, otherInput, adjInput, tipsInput, otInput]) {
    input.addEventListener("input", recompute);
  }

  const tryExample = tryExampleButton(() => {
    fields = { ...EXAMPLE };
    fsSelect.value = fields.fs;
    seniorSelect.value = String(fields.seniors);
    tipsInput.value = String(fields.tips);
    otInput.value = String(fields.overtime);
    stSelect.value = fields.st;
    vsSelect.value = "";
    wagesInput.value = String(fields.wages);
    otherInput.value = String(fields.other);
    adjInput.value = String(fields.adjustments);
    recompute();
  });

  const form = el(
    "form",
    {
      class: "tile-form",
      on: { submit: (e) => e.preventDefault() },
    },
    field("Filing status", fsSelect),
    field("State", stSelect),
    field("Annual wages", wagesInput),
    field("Other income", otherInput),
    field("Pre-tax adjustments", adjInput),
    field("Aged 65 or older", seniorSelect),
    field("Of that, qualified tips", tipsInput),
    field("Of that, qualified overtime premium", otInput),
    localContainer,
    field("Compare with another state", vsSelect),
    el("div", { class: "tile-form-actions" }, tryExample),
  );

  root.append(form, resultContainer);
  resolveLocal();
  renderLocalAddOns();
  compute();
}

export const takeHomeTile: TileDefinition = {
  id: "take-home",
  title: "Take-Home Pay",
  pillar: "paycheck",
  description: "Your real paycheck after federal, FICA, state, and local taxes, across all states.",
  keywords: ["paycheck", "net pay", "salary", "withholding", "fica", "state tax"],
  status: "ready",
  how:
    "Your take-home is your gross pay minus four things: federal income tax (the standard deduction, then the IRS bracket schedule), FICA (Social Security at 6.2% up to the annual wage base, plus Medicare at 1.45%, and a 0.9% Additional Medicare surtax on high earners), your state income tax, and any local tax that applies.\n\nThe effective rate is your total tax divided by gross income; the marginal rate is the bracket your next dollar lands in. Every line links the IRS or state source it came from.\n\nWe compute on the standard deduction, which is what about nine in ten filers take. If you itemize, the Federal Income Tax tool asks for the 'big four' and makes the comparison properly — this one does not ask for them, so it does not pretend to.\n\n" +
    OBBBA_DEDUCTIONS_HOW_NO_GIVING,
  resources: [
    {
      label: "IRS, federal income tax rates",
      url: "https://www.irs.gov/filing/federal-income-tax-rates-and-brackets",
    },
    {
      label: "IRS Publication 15-T (withholding)",
      url: "https://www.irs.gov/pub/irs-pdf/p15t.pdf",
    },
    {
      label: "SSA, Social Security & Medicare tax rates",
      url: "https://www.ssa.gov/oact/progdata/taxRates.html",
    },
  ],
  mount: mountTakeHome,
};
